import { SecondaryRegularButton } from 'components/ReusableComponents/Button/RegularButtons'
import { hotelCampaignRegistrationStatuses } from 'main/hotelStatuses'
import moment from 'moment'
import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import downloadAsCsv from 'utils/downloadAsCsv'

import { getHealthCheckBillingData } from '../../../actions/healthCheck'
import CustomHeader from '../../../components/ReusableComponents/HeaderTitles'
import LoadingIndicator from '../../../components/ReusableComponents/LoadingIndicator'
import Table from '../../../components/ReusableComponents/Table'
import Tab from '../../../components/ReusableComponents/Tabs'
import HealthCheckFilters from '../Common/Filters'
import useHealthCheckFilters from '../Common/useHealthCheckFilters'
import styles from './Billing.module.scss'
import { ReactComponent as ExcelIcon } from './excel.svg'
import BillingRow from './Row'
import BillingTitleRow from './TitleRow'
import TotalRow from './TotalRow'

function formatPrice(number) {
  return parseFloat(number).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD'
  })
}

const emailTypes = {
  SubsidiaryInvoiceForPayment: 'Payment',
  SubsidiaryInvoiceForReconciliation: 'Reconciliation',
  SubsidiaryInvoiceForPaymentLateMuch: 'Payment late'
}

const HealthCheckBilling = () => {
  const dispatch = useDispatch()

  const [results, setResults] = useState([])
  const [totalBarData, setTotalBarData] = useState()

  const [filterState, filterDispatch] = useHealthCheckFilters('billing')

  useEffect(() => {
    dispatch(getHealthCheckBillingData())
  }, [dispatch])

  const isLoading = useSelector(s => s.healthCheck.billings.isLoading)
  const hotelData = useSelector(s => s.healthCheck.billings.hotelData)
  const allhotelGroups = useSelector(s => s.healthCheck.billings.hotelGroups)
  const allhotelChains = useSelector(s => s.healthCheck.billings.hotelChains)

  const [sortByIndex, setSortByIndex] = useState(4)
  const [sortDecreasing, setSortDecreasing] = useState(true)

  function handleTitleCellClick(rowIndex) {
    if (sortByIndex === rowIndex) {
      setSortDecreasing(d => !d)
    }

    setSortByIndex(rowIndex)
  }

  function handleClickDownloadAsCsv() {
    const titleRow = [
      'Hotel ID',
      'Hotel name',
      'Is AutoPay enabled',
      'Balance',
      'Late balance',
      'Last payment amount',
      'Last payment date',
      'Last email type',
      'Last email date',
      'Notes'
    ]

    const resAsArrays = results.map(b => {
      return [
        b.displayId,
        `${b.brand}-${b.shortName}`,
        b.isAutoPayEnabled ? 'Yes' : 'No',
        formatPrice(b.balanceCurrent),
        formatPrice(b.balanceLate),
        formatPrice(b.paymentLastAmount),
        b.paymentLastReceivedOn
          ? moment(b.paymentLastReceivedOn).format('MM/DD/YYYY')
          : '-',
        b.emailLastContent ? emailTypes[b.emailLastContent] : '-',
        b.emailLastSentOn ? moment(b.emailLastSentOn).format('MM/DD/YYYY') : '-',
        localStorage.getItem(`healthCheck-billing-${b.subsidiaryId}`) || '-'
      ]
    })

    const dataArray = [titleRow, ...resAsArrays]
    const fileName = `health_check_billing.csv`

    downloadAsCsv(dataArray, fileName)
  }

  useEffect(() => {
    function getFilteredResults(hotelData) {
      const { searchString, chains, brands, hotelGroups, subsidiaryStatus, csmIds } =
        filterState

      return hotelData
        .filter(h => h.searchKey.match(new RegExp(searchString, 'i')))
        .filter(h => (hotelGroups === 'all' ? true : h.groupName === hotelGroups))
        .filter(h => (chains === 'all' ? true : h.chainCode === chains))
        .filter(h => (brands === 'all' ? true : h.parentChainCode === brands))
        .filter(h => {
          if (subsidiaryStatus.length === 0) {
            return true
          } else if (hotelCampaignRegistrationStatuses.includes(h.subsidiaryStatus)) {
            return subsidiaryStatus.includes('PendingCampaignBrandRegistration')
          } else {
            return subsidiaryStatus.includes(h.subsidiaryStatus)
          }
        })
        .filter(h => {
          if (csmIds.length === 0) {
            return true
          } else {
            return csmIds.includes(`${h.csmUserId}`)
          }
        })
    }

    function getSortedResults(hotelData, sortBy, sortDecreasing) {
      const titleRowNames = [
        'displayId',
        'shortName',
        'isAutoPayEnabled',
        'balanceCurrent',
        'balanceLate',
        'paymentLastAmount',
        'paymentLastReceivedOn',
        'emailLastSentOn'
      ]

      const sortByProp = titleRowNames[sortBy]

      const sortedData = hotelData.sort((a, b) => {
        if (sortByProp === 'paymentLastReceivedOn' || sortByProp === 'emailLastSentOn') {
          return (
            (b[sortByProp] ? Date.parse(b[sortByProp]) : 0) -
            (a[sortByProp] ? Date.parse(a[sortByProp]) : 0)
          )
        }

        if (typeof a[sortByProp] === 'string') {
          if (a[sortByProp] === b[sortByProp]) {
            return 0
          }
          return a[sortByProp]?.toUpperCase() > b[sortByProp]?.toUpperCase()
        } else if (typeof a[sortByProp] === 'boolean') {
          return Number(b[sortByProp]) - Number(a[sortByProp])
        } else {
          return b[sortByProp] - a[sortByProp]
        }
      })

      return sortDecreasing ? sortedData : sortedData.reverse()
    }

    function getResults() {
      const results = getSortedResults(
        getFilteredResults(hotelData),
        sortByIndex,
        sortDecreasing
      )

      setResults(results)

      const totalBarData = results.reduce(
        (prev, curr) => {
          return {
            balanceCurrent: prev.balanceCurrent + curr.balanceCurrent,
            balanceLate: prev.balanceLate + curr.balanceLate,
            paymentAmount: prev.paymentAmount + curr.paymentLastAmount,
            bookingsCharged:
              prev.totalBookingValueToCharge + curr.totalBookingValueToCharge,
            bookingsNotCharged:
              prev.totalBookingValueNotCharged + curr.totalBookingValueNotCharged
          }
        },
        {
          balanceLate: 0,
          balanceCurrent: 0,
          paymentAmount: 0
        }
      )

      setTotalBarData({ ...totalBarData, count: results.length })
    }

    getResults()
  }, [filterState, hotelData, sortByIndex, sortDecreasing])

  return (
    <div className={styles.wrapper}>
      <CustomHeader title="Hotel health check" width={1180} isFixed />
      <Tab.Parent>
        <Tab to="/healthCheck/pms">PMS</Tab>
        <Tab to="/healthCheck/autoinvite">AutoInvite</Tab>
        <Tab to="/healthCheck/billing">Billing</Tab>
        <Tab to="/healthCheck/performance">Performance</Tab>
      </Tab.Parent>
      <HealthCheckFilters
        state={filterState}
        dispatch={filterDispatch}
        allhotelGroups={allhotelGroups}
        allhotelChains={allhotelChains}
        showSubsidiaryStatus
        showCsmFilter
      >
        <SecondaryRegularButton
          className={styles.copyToExcelButton}
          onClick={() => handleClickDownloadAsCsv()}
        >
          <ExcelIcon /> Download as CSV
        </SecondaryRegularButton>
      </HealthCheckFilters>

      <div className={styles.tableContainer}>
        {isLoading ? (
          <LoadingIndicator marginTop={150} />
        ) : (
          <Table
            widths={[40, 240, 100, 100, 100, 110, 115, 125]}
            handleTitleCellClick={handleTitleCellClick}
            sortIndex={sortByIndex}
            sortDirection={sortDecreasing ? 'down' : 'up'}
          >
            <BillingTitleRow />
            {results.map((h, i) => (
              <BillingRow {...h} index={i + 1} />
            ))}
            <TotalRow {...totalBarData} />
          </Table>
        )}
      </div>
    </div>
  )
}

export default HealthCheckBilling
